import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Skeleton, Typography} from '@mui/material';
import {SplitTabsPanel} from '@/components/common';
import {queryAdjustmentList} from '@/services/auditService/auditAdjustOrchestrate';
import ASectionView from '@/pages/components/audit-program/ASectionView';
import BSectionView from '@/pages/components/audit-program/BSectionView';
import CSectionView from '@/pages/components/audit-program/CSectionView';
import DSectionView from '@/pages/components/audit-program/DSectionView';
import ESectionView from '@/pages/components/audit-program/ESectionView';
import FSectionView from '@/pages/components/audit-program/FSectionView';
import GSectionView from '@/pages/components/audit-program/GSectionView';
import HSectionView from '@/pages/components/audit-program/HSectionView';
import ISectionView from '@/pages/components/audit-program/ISectionView';
import JSectionView from '@/pages/components/audit-program/JSectionView';
import KSectionView from '@/pages/components/audit-program/KSectionView';
import LSectionView from '@/pages/components/audit-program/LSectionView';
import MSectionView from '@/pages/components/audit-program/MSectionView';
import NSectionView from '@/pages/components/audit-program/NSectionView';
import OSectionView from '@/pages/components/audit-program/OSectionView';
import PSectionView from '@/pages/components/audit-program/PSectionView';
import QSectionView from '@/pages/components/audit-program/QSectionView';
import RSectionView from '@/pages/components/audit-program/RSectionView';
import SSectionView from '@/pages/components/audit-program/SSectionView';
import TSectionView from '@/pages/components/audit-program/TSectionView';
import VSectionView from '@/pages/components/audit-program/VSectionView';
import WSectionView from '@/pages/components/audit-program/WSectionView';
import FilingIndexSectionView from '@/pages/components/audit-program/FilingIndexSectionView';
import PafSectionView from '@/pages/components/audit-program/PafSectionView';
import AppendixSectionView from '@/pages/components/audit-program/AppendixSectionView';
import {
    X_SECTION_INDEX,
    Y_SECTION_INDEX,
    Z_SECTION_INDEX,
} from '@/pages/components/audit-program/xyz-section/constants';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {deriveActiveIndexFromFs, type DeriveActiveIndexResult} from '@/pages/components/audit-program/ai-section/deriveActiveIndex';
import {
    buildProgramDirectory,
    convertVoToFsSimpleData,
    defaultDirectoryKey,
    type ProgramDirectoryItem,
} from '@/pages/components/audit-program/ai-section/programDirectory';

export type AuditProgramPanelProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
};

function SectionPlaceholder({item}: {item: ProgramDirectoryItem}) {
    return (
        <Box sx={{p: 3}}>
            <Typography variant="h6" gutterBottom>
                {item.code} — {item.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Coming soon — display only.
            </Typography>
        </Box>
    );
}

function renderDirectoryContent(
    item: ProgramDirectoryItem,
    companyId: number,
    annualId: number,
    companyName?: string | null,
    activeIndex?: DeriveActiveIndexResult | null,
) {
    if (item.view === 'A') {
        return (
            <ASectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
                initialPage={item.aSectionPage}
                activeIndex={activeIndex}
            />
        );
    }
    if (item.view === 'B') {
        return (
            <BSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'C') {
        return (
            <CSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'D') {
        return (
            <DSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'E') {
        return (
            <ESectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'F') {
        return (
            <FSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'G') {
        return (
            <GSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'H') {
        return (
            <HSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'I') {
        return (
            <ISectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'J') {
        return (
            <JSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'K') {
        return (
            <KSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'L') {
        return (
            <LSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'M') {
        return (
            <MSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'N') {
        return (
            <NSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'O') {
        return (
            <OSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'P') {
        return (
            <PSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'Q') {
        return (
            <QSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'R') {
        return (
            <RSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'S') {
        return (
            <SSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'T') {
        return (
            <TSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'V') {
        return (
            <VSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'W') {
        return (
            <WSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'X') {
        return (
            <FilingIndexSectionView
                sectionCode="X"
                pageCode="Xi"
                sectionLetter="X"
                sectionTitle="ACCOUNTS WORKING PAPERS"
                heading="X Section — Accounts working papers"
                rows={X_SECTION_INDEX}
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'Y') {
        return (
            <FilingIndexSectionView
                sectionCode="Y"
                pageCode="Yi"
                sectionLetter="Y"
                sectionTitle="OTHER PRIMARY FINANCIAL STATEMENTS"
                heading="Y Section — Other primary financial statements"
                rows={Y_SECTION_INDEX}
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'Z') {
        return (
            <FilingIndexSectionView
                sectionCode="Z"
                pageCode="Zi"
                sectionLetter="Z"
                sectionTitle="COMPUTER REPORTS AND RECORDS RECEIVED"
                heading="Z Section — Computer reports and records received"
                rows={Z_SECTION_INDEX}
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'PAF') {
        return (
            <PafSectionView
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    if (item.view === 'APPENDIX' && item.appendixCode) {
        return (
            <AppendixSectionView
                appendixCode={item.appendixCode}
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
            />
        );
    }
    return <SectionPlaceholder item={item}/>;
}

/**
 * Adjustment-page Audit Program host: BS/PL-driven directory (left) + section content (right).
 */
export default function AuditProgramPanel({companyId, annualId, companyName}: AuditProgramPanelProps) {
    const [directory, setDirectory] = useState<ProgramDirectoryItem[]>([]);
    const [activeIndex, setActiveIndex] = useState<DeriveActiveIndexResult | null>(null);
    const [sectionKey, setSectionKey] = useState('A');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadDirectory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const adjustData = await queryAdjustmentList({companyId, annualId});
            const bsLine = (adjustData.bsLine || []).map((n) =>
                convertVoToFsSimpleData(n as unknown as Record<string, unknown>),
            );
            const plLine = (adjustData.plLine || []).map((n) =>
                convertVoToFsSimpleData(n as unknown as Record<string, unknown>),
            );
            const active = deriveActiveIndexFromFs(bsLine, plLine);
            const items = buildProgramDirectory(bsLine, plLine);
            setActiveIndex(active);
            setDirectory(items);
            setSectionKey((prev) => (items.some((i) => i.key === prev) ? prev : defaultDirectoryKey(items)));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load program directory';
            setError(msg);
            setDirectory([]);
            setActiveIndex(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId]);

    useEffect(() => {
        void loadDirectory();
    }, [loadDirectory]);

    const tabs = useMemo(() => directory.map((item) => ({
        key: item.key,
        label: <PageTabLabel code={item.code} title={item.title}/>,
        // Collapsed rail shows section code (A / B / C…) instead of a bullet
        icon: item.code,
        content: renderDirectoryContent(item, companyId, annualId, companyName, activeIndex),
    })), [directory, companyId, annualId, companyName, activeIndex]);

    if (loading && directory.length === 0) {
        return <Skeleton variant="rectangular" height="100%" sx={{m: 1}}/>;
    }

    if (error && directory.length === 0) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0}}>
            <SplitTabsPanel
                tabs={tabs}
                selectedKey={sectionKey}
                onChange={setSectionKey}
                height="100%"
                allowMaximize={false}
                errorMsg={error}
                leftPaneSx={{minWidth: 180}}
                contentWrapperSx={{height: '100%', overflow: 'hidden', p: 0}}
                rightPaneSx={{height: '100%', overflow: 'hidden'}}
            />
        </Box>
    );
}
